import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  fetchInstanceById,
  fetchAuditLog,
  parseResponseJson,
  parseSchemaJson,
  saveDraftResponse,
  submitForReview,
} from "../esource/esourceService";
import {
  applyFieldDefaults,
  collectFieldValidationIssues,
  collectRequiredMissing,
  evaluateLogicVisibility,
} from "../esource/esourceFieldTypes";
import { ESourceFormFields } from "../esource/esourceFormRender";
import {
  isFormStudioSchema,
  parseFormStudioDocument,
} from "../esource/ESourceFormStudio";
import InfoAlert from "../../common/InfoAlert";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import { openESignatureConfirmModal } from "../../common/esignature/esignatureConfirmService";
import useInfoAlert from "../../../hooks/useInfoAlert";
import {
  approveFormWorkItem,
  completeFormOccurrence,
  fetchFormKitContext,
  fetchFormLifecycle,
  rejectFormWorkItem,
  submitFormWorkItem,
} from "./epsfService";
import {
  formatFormTriggerLabel,
  formatReceiptStatusLabel,
  renderReceiptStatusBadge,
} from "./epsfBadges";
import "../esource/ESourcePages.scss";
import "./EpsfPages.scss";

const FORM_STUDIO_SRC = `${process.env.PUBLIC_URL || ""}/form-studio/index.html?v=titlefix1`;

const FORM_ZOOM_MIN = 0.5;
const FORM_ZOOM_MAX = 1.75;
const FORM_ZOOM_STEP = 0.1;
const FORM_ZOOM_DEFAULT = 1;

function clampFormZoom(z) {
  const n = Number(z);
  if (!Number.isFinite(n)) return FORM_ZOOM_DEFAULT;
  return Math.min(
    FORM_ZOOM_MAX,
    Math.max(FORM_ZOOM_MIN, Math.round(n * 100) / 100),
  );
}

function FormZoomControls({ zoom, onZoomChange, disabled = false }) {
  const pct = Math.round(clampFormZoom(zoom) * 100);
  return (
    <div className="epsf-form-zoom" role="group" aria-label="Form zoom">
      <button
        type="button"
        className="epsf-form-zoom__btn"
        title="Zoom out"
        aria-label="Zoom out"
        disabled={disabled || zoom <= FORM_ZOOM_MIN}
        onClick={() =>
          onZoomChange(clampFormZoom(zoom / (1 + FORM_ZOOM_STEP)))
        }
      >
        −
      </button>
      <button
        type="button"
        className="epsf-form-zoom__label"
        title="Reset zoom to 100%"
        aria-label={`Zoom ${pct} percent. Reset to 100 percent`}
        disabled={disabled}
        onClick={() => onZoomChange(FORM_ZOOM_DEFAULT)}
      >
        {pct}%
      </button>
      <button
        type="button"
        className="epsf-form-zoom__btn"
        title="Zoom in"
        aria-label="Zoom in"
        disabled={disabled || zoom >= FORM_ZOOM_MAX}
        onClick={() =>
          onZoomChange(clampFormZoom(zoom * (1 + FORM_ZOOM_STEP)))
        }
      >
        +
      </button>
    </div>
  );
}

const AUDIT_ACTION_META = {
  Initiated: { label: "Form initiated", tone: "neutral" },
  FillAvailable: { label: "Opened for fill", tone: "info" },
  FillDue: { label: "Fill due", tone: "warn" },
  Filled: { label: "Filled", tone: "info" },
  ApprovalAvailable: { label: "Opened for approval", tone: "info" },
  ApprovalDue: { label: "Approval due", tone: "warn" },
  CorrectionAvailable: { label: "Opened for correction", tone: "warn" },
  CorrectionDue: { label: "Correction due", tone: "warn" },
  CorrectionSubmitted: { label: "Correction submitted", tone: "info" },
  InstanceCreated: { label: "Form record created", tone: "neutral" },
  DraftSaved: { label: "Draft saved", tone: "neutral" },
  SubmittedForReview: { label: "Submitted for approval", tone: "info" },
  Submitted: { label: "Submitted for approval", tone: "info" },
  Approved: { label: "Approved", tone: "success" },
  Locked: { label: "Locked", tone: "success" },
  Rejected: { label: "Rejected — correction required", tone: "danger" },
  Amended: { label: "Amended", tone: "warn" },
};

function formatAuditWhen(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAuditAction(action) {
  const key = String(action || "").trim();
  if (AUDIT_ACTION_META[key]) return AUDIT_ACTION_META[key].label;
  if (!key) return "Update";
  return key.replace(/([a-z])([A-Z])/g, "$1 $2").trim();
}

function auditTone(action) {
  return AUDIT_ACTION_META[String(action || "").trim()]?.tone || "neutral";
}

function FormActivityLog({ rows, loading }) {
  if (loading) {
    return <p className="epsf-form-sheet__audit-empty">Loading activity…</p>;
  }
  if (!rows.length) {
    return (
      <p className="epsf-form-sheet__audit-empty">
        No activity recorded for this form yet.
      </p>
    );
  }
  return (
    <ol className="epsf-form-sheet__audit-list">
      {rows.map((row, index) => {
        const action = row.Action || row.action || "";
        const who =
          String(row.PerformedByName || row.performedByName || "").trim() ||
          "Staff";
        const notes = String(row.Reason || row.reason || "").trim();
        return (
          <li
            key={String(row.AuditId || row.auditId || `${action}-${index}`)}
            className={`epsf-form-sheet__audit-item is-${auditTone(action)}`}
          >
            <div className="epsf-form-sheet__audit-dot" aria-hidden="true" />
            <div className="epsf-form-sheet__audit-body">
              <div className="epsf-form-sheet__audit-title">
                {formatAuditAction(action)}
              </div>
              <div className="epsf-form-sheet__audit-meta">
                <span>{formatAuditWhen(row.PerformedAt || row.performedAt)}</span>
                <span aria-hidden="true">·</span>
                <span>{who}</span>
              </div>
              {notes ? (
                <div className="epsf-form-sheet__audit-reason">
                  <span className="epsf-form-sheet__audit-notes-label">Notes</span>
                  {notes}
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function groupKitsByStage(kits) {
  const map = new Map();
  (kits || []).forEach((kit) => {
    const stage =
      String(kit.StageLabel || kit.stageLabel || kit.Status || kit.status || "").trim() ||
      "Unknown";
    if (!map.has(stage)) map.set(stage, []);
    map.get(stage).push(kit);
  });
  return Array.from(map.entries()).map(([stage, rows]) => ({ stage, rows }));
}

function formatKitContextLabel(raw) {
  const value = String(raw || "").trim();
  if (!value) return "Linked transaction";
  const key = value.toLowerCase().replace(/[\s_-]+/g, "");
  if (key.includes("receipt")) return "Receipt";
  if (key.includes("dispense")) return "Dispense";
  if (key.includes("destroy")) return "Destruction";
  if (key.includes("return")) return "Return";
  if (key.includes("reconcil")) return "Reconcile";
  return value.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");
}

/**
 * Compact left-rail context for ePSF form fill.
 * Shows: product/batch summary, kits on this linked action, and a short batch overview.
 */
function FormKitContextPanel({ summary, kits, loading, message }) {
  if (loading) {
    return <p className="epsf-form-sheet__kits-empty">Loading batch…</p>;
  }
  if (!summary) {
    return (
      <p className="epsf-form-sheet__kits-empty">
        {message ||
          "No linked batch for this form yet. Open from a receipt, dispense, or destruction row to see kit context here."}
      </p>
    );
  }

  const batchNo =
    String(summary.BatchNumber || summary.batchNumber || "").trim() || "—";
  const context = formatKitContextLabel(
    summary.ContextLabel || summary.contextLabel,
  );
  const product =
    String(summary.ProductName || summary.productName || "").trim();
  const productCode = String(
    summary.ProductCode || summary.productCode || "",
  ).trim();
  const refNo = String(summary.ReferenceNo || summary.referenceNo || "").trim();
  const txnCount = Number(summary.TxnKitCount ?? summary.txnKitCount ?? 0);
  const batchCount = Number(
    summary.BatchKitCount ?? summary.batchKitCount ?? kits.length,
  );
  const inTxn = (kits || []).filter(
    (k) => Number(k.InThisTxn ?? k.inThisTxn ?? 0) === 1,
  );
  const focusKits = inTxn.length ? inTxn : kits || [];
  const otherKits = inTxn.length
    ? (kits || []).filter((k) => Number(k.InThisTxn ?? k.inThisTxn ?? 0) !== 1)
    : [];
  const focusStages = groupKitsByStage(focusKits);
  const otherStages = groupKitsByStage(otherKits);
  const showBatchOverview = otherStages.length > 0;
  const formKitCount = txnCount || inTxn.length || focusKits.length;
  const contextTitle =
    context === "Destruction"
      ? "Kits on this destruction"
      : context === "Dispense"
        ? "Kits on this dispense"
        : context === "Return"
          ? "Kits on this return"
          : context === "Receipt"
            ? "Kits on this receipt"
            : "Kits on this form";

  return (
    <div className="epsf-form-sheet__kits-body">
      <div className="epsf-form-sheet__kits-batch">
        <div className="epsf-form-sheet__kits-batch-no">{batchNo}</div>
        {product || productCode ? (
          <div className="epsf-form-sheet__kits-product">
            {[productCode, product].filter(Boolean).join(" · ")}
          </div>
        ) : null}
        <div className="epsf-form-sheet__kits-meta">
          <span className="epsf-form-sheet__kits-context">{context}</span>
          {refNo ? (
            <span className="epsf-form-sheet__kits-ref">Ref {refNo}</span>
          ) : null}
        </div>
        <div className="epsf-form-sheet__kits-counts" role="group" aria-label="Kit counts">
          <div>
            <span>On this form</span>
            <strong>{formKitCount}</strong>
          </div>
          <div>
            <span>On batch</span>
            <strong>{batchCount}</strong>
          </div>
        </div>
      </div>

      <div className="epsf-form-sheet__kits-section">
        <h4 className="epsf-form-sheet__kits-section-title">
          {contextTitle}
        </h4>
        {focusStages.length ? (
          focusStages.map(({ stage, rows }) => (
            <div key={`focus-${stage}`} className="epsf-form-sheet__kits-stage">
              <div className="epsf-form-sheet__kits-stage-head">
                {renderReceiptStatusBadge(stage) || (
                  <span>{formatReceiptStatusLabel(stage) || stage}</span>
                )}
                <span className="epsf-form-sheet__kits-stage-count">
                  {rows.length}
                </span>
              </div>
              <ul className="epsf-form-sheet__kits-chips">
                {rows.map((kit) => (
                  <li
                    key={String(kit.ImpKitId || kit.impKitId || kit.KitNumber)}
                    className="epsf-form-sheet__kits-chip is-focus"
                    title={kit.KitNumber || kit.kitNumber || ""}
                  >
                    {kit.KitNumber || kit.kitNumber || "—"}
                  </li>
                ))}
              </ul>
            </div>
          ))
        ) : (
          <p className="epsf-form-sheet__kits-empty">
            No kits linked to this form yet.
          </p>
        )}
      </div>

      {showBatchOverview ? (
        <div className="epsf-form-sheet__kits-section">
          <h4 className="epsf-form-sheet__kits-section-title">
            Rest of batch
          </h4>
          <ul className="epsf-form-sheet__kits-overview">
            {otherStages.map(({ stage, rows }) => (
              <li key={`ov-${stage}`} className="epsf-form-sheet__kits-overview-row">
                <span className="epsf-form-sheet__kits-overview-label">
                  {formatReceiptStatusLabel(stage) || stage}
                </span>
                <span className="epsf-form-sheet__kits-overview-count">
                  {rows.length}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Fill / review / view UI for an e-Source form linked to an ePSF work item.
 * Form Studio: designed page is the modal; fill uses the form Submit button.
 * Review/view: read-only answers + Approve (review) in the sheet chrome.
 * Locked/view also shows the instance activity log (who submitted / approved / rejected).
 */
export default function EpsfFormFillModal({
  open,
  formInstanceId,
  workItem = null,
  title = "Complete form",
  mode = "fill",
  onClose,
  onSubmitted,
  onApproved,
}) {
  const sheetMode = String(mode || "fill").toLowerCase();
  const isReview = sheetMode === "review";
  const isView =
    sheetMode === "view" ||
    sheetMode === "locked" ||
    sheetMode === "pending";
  const isReadOnly = isReview || isView;
  const showAudit = isReadOnly;

  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [instance, setInstance] = useState(null);
  const [values, setValues] = useState({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [studioReady, setStudioReady] = useState(false);
  const [formZoom, setFormZoom] = useState(FORM_ZOOM_DEFAULT);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [kitSummary, setKitSummary] = useState(null);
  const [kitRows, setKitRows] = useState([]);
  const [kitLoading, setKitLoading] = useState(false);
  const [kitMessage, setKitMessage] = useState("");
  const iframeRef = useRef(null);
  const completeRef = useRef(null);
  const busyRef = useRef(false);
  const valuesRef = useRef({});

  const linkedTxnId =
    workItem?.LinkedTxnId ||
    workItem?.linkedTxnId ||
    null;
  const occurrenceIdForKits =
    workItem?.FormOccurrenceId || workItem?.formOccurrenceId || null;
  /* Always show left Batch & kits panel for ePSF work-item forms (receipt/dispense/destroy). */
  const showKitContext = Boolean(workItem || linkedTxnId || occurrenceIdForKits);

  const schemaJson = instance?.SchemaJson || instance?.schemaJson || "";
  const isStudio = useMemo(
    () => isFormStudioSchema(schemaJson),
    [schemaJson],
  );
  const studioDoc = useMemo(
    () => (isStudio ? parseFormStudioDocument(schemaJson) : null),
    [isStudio, schemaJson],
  );

  const getStudioHost = useCallback(() => {
    try {
      return iframeRef.current?.contentWindow?.AQ_FORM_STUDIO_HOST || null;
    } catch {
      return null;
    }
  }, []);

  const applyFormZoom = useCallback(
    (nextZoom) => {
      const z = clampFormZoom(nextZoom);
      setFormZoom(z);
      if (!isStudio) return;
      const host = getStudioHost();
      if (host && typeof host.setZoom === "function") {
        host.setZoom(z);
      }
    },
    [getStudioHost, isStudio],
  );

  /* Keep Form Studio iframe zoom in sync with header controls. */
  useEffect(() => {
    if (!open || !isStudio || !studioReady) return;
    const host = getStudioHost();
    if (host && typeof host.setZoom === "function") {
      host.setZoom(formZoom);
    }
  }, [open, isStudio, studioReady, formZoom, getStudioHost]);

  const load = useCallback(async () => {
    if (!formInstanceId) return;
    setLoading(true);
    setMessage("");
    setStudioReady(false);
    try {
      const res = await fetchInstanceById(formInstanceId);
      if (!res.success || !res.instance) {
        setInstance(null);
        setMessage(res.message || "Form record not found.");
        return;
      }
      setInstance(res.instance);
      const schema = parseSchemaJson(res.instance.SchemaJson);
      const nextValues = applyFieldDefaults(
        schema.fields,
        parseResponseJson(res.instance.ResponseJson),
      );
      /* Form Studio answers are keyed by field key in ResponseJson */
      const raw = parseResponseJson(res.instance.ResponseJson);
      const studioValues =
        raw && typeof raw === "object" && !Array.isArray(raw) ? raw : nextValues;
      setValues(isFormStudioSchema(res.instance.SchemaJson) ? studioValues : nextValues);
    } finally {
      setLoading(false);
    }
  }, [formInstanceId]);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    if (!open || !formInstanceId) {
      setInstance(null);
      setValues({});
      setMessage("");
      setStudioReady(false);
      setFormZoom(FORM_ZOOM_DEFAULT);
      setAuditRows([]);
      setKitSummary(null);
      setKitRows([]);
      setKitMessage("");
      return;
    }
    setFormZoom(FORM_ZOOM_DEFAULT);
    load();
  }, [open, formInstanceId, load]);

  useEffect(() => {
    if (!open || !showKitContext) {
      setKitSummary(null);
      setKitRows([]);
      setKitMessage("");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setKitLoading(true);
      setKitMessage("");
      try {
        const res = await fetchFormKitContext({
          FormInstanceId: formInstanceId || null,
          FormOccurrenceId: occurrenceIdForKits,
          WorkItemId: workItem?.WorkItemId || workItem?.workItemId || null,
          LinkedTxnId: linkedTxnId,
          StudyId: workItem?.StudyId || workItem?.studyId || null,
          SiteId: workItem?.SiteId || workItem?.siteId || null,
        });
        if (cancelled) return;
        const summary = res.summary;
        const hasBatch = Boolean(
          summary &&
            (summary.ImpBatchId ||
              summary.impBatchId ||
              summary.BatchNumber ||
              summary.batchNumber ||
              summary.ImpTxnId ||
              summary.impTxnId),
        );
        setKitSummary(hasBatch ? summary : null);
        setKitRows(Array.isArray(res.kits) ? res.kits : []);
        if (!hasBatch) {
          setKitMessage(
            res.message ||
              "No linked batch found for this form occurrence.",
          );
        }
      } catch (err) {
        if (!cancelled) {
          setKitSummary(null);
          setKitRows([]);
          setKitMessage(
            err?.message?.includes("404")
              ? "Batch API is unavailable. Restart the local API and try again."
              : err?.message || "Could not load batch information.",
          );
        }
      } finally {
        if (!cancelled) setKitLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    showKitContext,
    formInstanceId,
    occurrenceIdForKits,
    linkedTxnId,
    workItem,
  ]);

  useEffect(() => {
    if (!open || !showAudit) {
      setAuditRows([]);
      return undefined;
    }
    const occurrenceId =
      workItem?.FormOccurrenceId ||
      workItem?.formOccurrenceId ||
      null;
    const workItemId = workItem?.WorkItemId || workItem?.workItemId || null;
    if (!formInstanceId && !occurrenceId && !workItemId) {
      setAuditRows([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setAuditLoading(true);
      try {
        const res = await fetchFormLifecycle({
          FormInstanceId: formInstanceId || null,
          FormOccurrenceId: occurrenceId,
          WorkItemId: workItemId,
        });
        if (cancelled) return;
        if (res.success && Array.isArray(res.rows) && res.rows.length) {
          setAuditRows(res.rows);
          return;
        }
        /* Fallback to e-Source audit if lifecycle has no occurrence yet */
        if (formInstanceId) {
          const auditRes = await fetchAuditLog({ FormInstanceId: formInstanceId });
          if (cancelled) return;
          setAuditRows(Array.isArray(auditRes.rows) ? auditRes.rows : []);
        } else {
          setAuditRows([]);
        }
      } catch {
        if (!cancelled) setAuditRows([]);
      } finally {
        if (!cancelled) setAuditLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, formInstanceId, showAudit, workItem]);

  const fields = useMemo(
    () => parseSchemaJson(instance?.SchemaJson).fields,
    [instance],
  );
  const schemaExtras = useMemo(() => {
    const schema = parseSchemaJson(instance?.SchemaJson);
    return {
      logic: schema.logic || [],
      layout: schema.settings?.layout || "two-column",
    };
  }, [instance]);
  const formIntro = useMemo(() => {
    const schema = parseSchemaJson(instance?.SchemaJson);
    return {
      header: schema.formHeader || "",
      description: schema.formDescription || "",
    };
  }, [instance]);

  const status = String(instance?.Status || "");
  const isLockedStatus =
    status === "Locked" || status === "Signed" || !!instance?.LockedAt;
  const isEditable =
    !isReadOnly &&
    !isLockedStatus &&
    (status === "Draft" || status === "InReview" || !status);
  const forceReadOnly = isReadOnly || isLockedStatus;

  const setFieldValue = (fieldId, value) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const completeEpsfWork = useCallback(
    async (responseJson) => {
      if (forceReadOnly) {
        setMessage("This form is locked and cannot be changed.");
        return false;
      }
      const saved = await saveDraftResponse({
        FormInstanceId: formInstanceId,
        ResponseJson: responseJson,
        Title: instance?.Title || title,
      });
      if (!saved.success) {
        setMessage(saved.message || "Could not save form answers.");
        return false;
      }

      try {
        await submitForReview({
          FormInstanceId: formInstanceId,
          ResponseJson: responseJson,
          Title: instance?.Title || title,
        });
      } catch {
        /* ePSF work item is the pharmacy gate */
      }

      const occurrenceId =
        workItem?.FormOccurrenceId || workItem?.formOccurrenceId;
      const workItemId = workItem?.WorkItemId || workItem?.workItemId;
      const result = workItemId
        ? await submitFormWorkItem({
            WorkItemId: workItemId,
            FormOccurrenceId: occurrenceId,
            FormInstanceId: formInstanceId,
          })
        : await completeFormOccurrence({
            FormOccurrenceId: occurrenceId,
            FormInstanceId: formInstanceId,
          });

      if (!result.success) {
        setMessage(
          result.message || "Form saved but could not mark due complete.",
        );
        return false;
      }

      if (typeof onSubmitted === "function") {
        await onSubmitted(result);
      }
      if (typeof onClose === "function") onClose();
      return true;
    },
    [forceReadOnly, formInstanceId, instance, title, workItem, onSubmitted, onClose],
  );

  completeRef.current = completeEpsfWork;

  const enterStudioFill = useCallback(() => {
    const host = getStudioHost();
    if (!host || !studioDoc) return false;

    if (!forceReadOnly) {
      host._onFillSubmit = async (data) => {
        if (busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        setMessage("");
        try {
          await completeRef.current?.(JSON.stringify(data || {}));
        } finally {
          busyRef.current = false;
          setBusy(false);
        }
      };
    } else if (host._onFillSubmit) {
      try {
        delete host._onFillSubmit;
      } catch {
        /* ignore */
      }
    }

    if (typeof host.enterFillMode === "function") {
      host.enterFillMode(studioDoc);
    } else if (typeof host.setDocument === "function") {
      host.setDocument(studioDoc);
    }

    if (typeof host.setFillValues === "function") {
      host.setFillValues(valuesRef.current || {});
    }
    if (typeof host.setFillReadOnly === "function") {
      host.setFillReadOnly(forceReadOnly);
    }

    setStudioReady(true);
    return true;
  }, [getStudioHost, studioDoc, forceReadOnly]);

  useEffect(() => {
    if (!open || !isStudio || !studioDoc) return undefined;

    const onMessage = (event) => {
      const data = event?.data;
      if (!data || data.source !== "aq-form-studio") return;
      if (data.type === "ready") {
        enterStudioFill();
        return;
      }
      if (data.type === "zoom") {
        const z = clampFormZoom(data.payload?.zoom);
        setFormZoom((prev) => (prev === z ? prev : z));
      }
    };

    window.addEventListener("message", onMessage);
    enterStudioFill();

    return () => {
      window.removeEventListener("message", onMessage);
      const host = getStudioHost();
      if (host) {
        try {
          delete host._onFillSubmit;
          host.setFillReadOnly?.(false);
        } catch {
          /* ignore */
        }
      }
    };
  }, [open, isStudio, studioDoc, enterStudioFill, getStudioHost]);

  /* Re-apply answers once instance values load into an already-ready studio */
  useEffect(() => {
    if (!open || !isStudio || !studioReady) return;
    const host = getStudioHost();
    if (!host) return;
    host.setFillValues?.(values || {});
    host.setFillReadOnly?.(forceReadOnly);
  }, [open, isStudio, studioReady, values, forceReadOnly, getStudioHost]);

  const onClassicSubmit = async () => {
    if (forceReadOnly) {
      setMessage("This form is locked and cannot be changed.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const { visible } = evaluateLogicVisibility(
        fields,
        values,
        schemaExtras.logic,
      );
      const missing = collectRequiredMissing(fields, values, visible);
      if (missing.length) {
        setMessage(
          `Please complete required fields: ${missing.map((f) => f.label).join(", ")}`,
        );
        return;
      }
      const issues = collectFieldValidationIssues(fields, values, visible);
      if (issues.length) {
        setMessage(issues.map((i) => i.message).join(" "));
        return;
      }
      await completeEpsfWork(JSON.stringify(values));
    } finally {
      setBusy(false);
    }
  };

  const runApproveAfterConfirm = async () => {
    const sign = await openESignatureConfirmModal({
      title: "Sign approval",
    });
    if (
      !sign ||
      sign.cancelled ||
      Number(sign.Success ?? sign.success ?? 0) !== 1 ||
      !sign.SignatureBase64
    ) {
      return;
    }

    const auth = await openAuthModal({
      introMessage:
        "Re-authenticate to approve and lock this form. Approval notes are required.",
      showDescription: true,
      requireDescription: true,
      descriptionLabel: "Approval notes",
      descriptionPlaceholder: "Enter notes for this approval…",
      descriptionMaxLength: 1000,
    });
    if (
      !auth ||
      auth.cancelled ||
      Number(auth.Success ?? auth.success ?? 0) !== 1
    ) {
      return;
    }
    const notes = String(auth.Description || auth.description || "").trim();
    if (!notes) {
      setMessage("Approval notes are required.");
      return;
    }

    setBusy(true);
    setMessage("");
    try {
      const occurrenceId =
        workItem?.FormOccurrenceId || workItem?.formOccurrenceId;
      const workItemId = workItem?.WorkItemId || workItem?.workItemId;
      /* ApproveWorkItem locks the e-Source instance in SQL (Status = Locked).
         RejectionReason carries approval notes into FormAudit.Reason. */
      const result = await approveFormWorkItem({
        WorkItemId: workItemId,
        FormOccurrenceId: occurrenceId,
        FormInstanceId: formInstanceId,
        RejectionReason: notes,
      });
      if (!result.success) {
        setMessage(result.message || "Could not approve this form.");
        return;
      }
      if (typeof onApproved === "function") {
        await onApproved(result);
      }
      if (typeof onClose === "function") onClose();
    } finally {
      setBusy(false);
    }
  };

  const onApprove = () => {
    if (busy) return;
    showAlert({
      title: "Confirm approval",
      message:
        "Are you sure you want to approve and lock this form? You must sign and authenticate to complete this action.",
      type: "warning",
      buttonText: "Continue",
      cancelText: "Cancel",
      onConfirm: async () => {
        hideAlert();
        await runApproveAfterConfirm();
      },
    });
  };

  const runRejectAfterConfirm = async () => {
    const sign = await openESignatureConfirmModal({
      title: "Sign rejection",
    });
    if (
      !sign ||
      sign.cancelled ||
      Number(sign.Success ?? sign.success ?? 0) !== 1 ||
      !sign.SignatureBase64
    ) {
      return;
    }

    const auth = await openAuthModal({
      introMessage:
        "Re-authenticate to reject this form and request correction. Rejection notes are required.",
      showDescription: true,
      requireDescription: true,
      descriptionLabel: "Rejection notes",
      descriptionPlaceholder: "Explain what must be corrected…",
      descriptionMaxLength: 1000,
    });
    if (
      !auth ||
      auth.cancelled ||
      Number(auth.Success ?? auth.success ?? 0) !== 1
    ) {
      return;
    }
    const reason = String(auth.Description || auth.description || "").trim();
    if (!reason) {
      setMessage("Rejection notes are required.");
      return;
    }

    setBusy(true);
    setMessage("");
    try {
      const occurrenceId =
        workItem?.FormOccurrenceId || workItem?.formOccurrenceId;
      const workItemId = workItem?.WorkItemId || workItem?.workItemId;
      const result = await rejectFormWorkItem({
        WorkItemId: workItemId,
        FormOccurrenceId: occurrenceId,
        FormInstanceId: formInstanceId,
        RejectionReason: reason,
      });
      if (!result.success) {
        setMessage(result.message || "Could not reject this form.");
        return;
      }
      if (typeof onApproved === "function") {
        await onApproved(result);
      }
      if (typeof onClose === "function") onClose();
    } finally {
      setBusy(false);
    }
  };

  const onReject = () => {
    if (busy) return;
    showAlert({
      title: "Confirm rejection",
      message:
        "Are you sure you want to reject this form and request correction? You must sign and authenticate to complete this action.",
      type: "warning",
      buttonText: "Continue",
      cancelText: "Cancel",
      onConfirm: async () => {
        hideAlert();
        await runRejectAfterConfirm();
      },
    });
  };

  if (!open) return null;

  const rawHeading =
    instance?.Title ||
    workItem?.Title ||
    workItem?.title ||
    workItem?.TemplateName ||
    title;
  const heading = (() => {
    const text = String(rawHeading || "").trim();
    if (!text) return title;
    // Titles like "DESTRUCTION_REQUESTED 2026-09-26 …" → "Destruction requested 2026-09-26 …"
    const spaceIdx = text.indexOf(" ");
    const code = spaceIdx > 0 ? text.slice(0, spaceIdx) : text;
    const rest = spaceIdx > 0 ? text.slice(spaceIdx) : "";
    if (/^[A-Z][A-Z0-9_]+$/.test(code)) {
      return `${formatFormTriggerLabel(code)}${rest}`;
    }
    return text;
  })();

  const modeLabel = isReview
    ? "Approval pending"
    : sheetMode === "pending"
      ? "Approval pending"
      : sheetMode === "locked" || sheetMode === "view"
        ? "Approved (locked)"
        : "Complete form";

  /* Form Studio / loading: designed page is the modal. */
  if (loading || !instance || isStudio) {
    const sheetClass = [
      "epsf-form-sheet",
      showAudit ? "epsf-form-sheet--with-audit" : "",
      showKitContext ? "epsf-form-sheet--with-kits" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return createPortal(
      <>
      <div
        className={sheetClass}
        role="dialog"
        aria-modal="true"
        aria-labelledby="epsf-form-sheet-title"
      >
        <button
          type="button"
          className="epsf-form-sheet__backdrop"
          aria-label="Close form"
          onClick={busy ? undefined : onClose}
          disabled={busy}
        />
        <div className="epsf-form-sheet__panel">
          <div className="epsf-form-sheet__header">
            <div className="epsf-form-sheet__header-text">
              <h2 className="epsf-form-sheet__title" id="epsf-form-sheet-title">
                {heading}
              </h2>
              <span className="epsf-form-sheet__mode">{modeLabel}</span>
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
              disabled={busy}
            />
          </div>
          {message ? (
            <div className="epsf-form-sheet__alert" role="alert">
              {message}
            </div>
          ) : null}
          {busy ? (
            <div className="epsf-form-sheet__busy" aria-live="polite">
              {isReview ? "Saving…" : "Saving…"}
            </div>
          ) : null}
          <div className="epsf-form-sheet__layout">
            {showKitContext ? (
              <aside
                className="epsf-form-sheet__kits"
                aria-label="Batch and kit context"
              >
                <h3 className="epsf-form-sheet__kits-heading">Batch &amp; kits</h3>
                <FormKitContextPanel
                  summary={kitSummary}
                  kits={kitRows}
                  loading={kitLoading}
                  message={kitMessage}
                />
              </aside>
            ) : null}
            <div className="epsf-form-sheet__main">
              {loading ? (
                <p className="epsf-form-sheet__hint">Loading form…</p>
              ) : !instance ? (
                <p className="epsf-form-sheet__hint">
                  {message || "Form could not be loaded."}
                </p>
              ) : (
                <div className="epsf-form-sheet__studio">
                  <FormZoomControls
                    zoom={formZoom}
                    onZoomChange={applyFormZoom}
                    disabled={busy}
                  />
                  {!studioReady ? (
                    <p className="epsf-form-sheet__hint">Preparing form…</p>
                  ) : null}
                  <iframe
                    ref={iframeRef}
                    title={heading}
                    src={FORM_STUDIO_SRC}
                    className="epsf-form-sheet__frame"
                  />
                </div>
              )}
            </div>
            {showAudit && instance ? (
              <aside
                className="epsf-form-sheet__audit"
                aria-label="Form activity log"
              >
                <h3 className="epsf-form-sheet__audit-heading">Activity</h3>
                <FormActivityLog rows={auditRows} loading={auditLoading} />
              </aside>
            ) : null}
          </div>
          {instance ? (
            <div className="epsf-form-sheet__actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={busy}
              >
                {isReadOnly && !isReview ? "Close" : "Cancel"}
              </button>
              {isReview ? (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={onReject}
                    disabled={busy}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onApprove}
                    disabled={busy}
                  >
                    {busy ? "Approving…" : "Approve & lock"}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <InfoAlert {...alertState} onClose={hideAlert} />
      </>,
      document.body,
    );
  }

  /* Classic field builder */
  return createPortal(
    <>
    <div
      className="modal fade show d-block epsf-modal epsf-form-fill-modal"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="epsf-form-fill-title"
    >
      <div className="modal-dialog modal-xl epsf-form-fill-modal__dialog">
        <div className="modal-content epsf-form-fill-modal__content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title" id="epsf-form-fill-title">
                {heading}
              </h5>
              <p className="epsf-form-fill-modal__sub">{modeLabel}</p>
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
              disabled={busy}
            />
          </div>
          <div className="modal-body epsf-form-fill-modal__body">
            {message ? (
              <div className="epsf-receive__error" role="alert">
                {message}
              </div>
            ) : null}
            <div className="epsf-form-fill-modal__layout">
              {showKitContext ? (
                <aside
                  className="epsf-form-sheet__kits epsf-form-fill-modal__kits"
                  aria-label="Batch and kit context"
                >
                  <h3 className="epsf-form-sheet__kits-heading">
                    Batch &amp; kits
                  </h3>
                  <FormKitContextPanel
                    summary={kitSummary}
                    kits={kitRows}
                    loading={kitLoading}
                    message={kitMessage}
                  />
                </aside>
              ) : null}
              <div className="epsf-form-fill-modal__form-wrap">
                <FormZoomControls
                  zoom={formZoom}
                  onZoomChange={applyFormZoom}
                  disabled={busy}
                />
                <div
                  className="esource-card esource-card--flush epsf-form-fill-modal__form"
                  style={{
                    transform: `scale(${formZoom})`,
                    transformOrigin: "top left",
                    width: formZoom === 1 ? undefined : `${100 / formZoom}%`,
                  }}
                >
                  {formIntro.header || formIntro.description ? (
                    <div className="esource-form-intro-block">
                      {formIntro.header ? (
                        <h2 className="esource-form-intro-block__header">
                          {formIntro.header}
                        </h2>
                      ) : null}
                      {formIntro.description ? (
                        <p className="esource-form-intro-block__description">
                          {formIntro.description}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  <ESourceFormFields
                    fields={fields}
                    values={values}
                    disabled={!isEditable || busy}
                    onChange={setFieldValue}
                    emptyText="This template has no fields."
                    logic={schemaExtras.logic}
                    layout={schemaExtras.layout}
                  />
                </div>
              </div>
              {showAudit ? (
                <aside
                  className="epsf-form-fill-modal__audit"
                  aria-label="Form activity log"
                >
                  <h3 className="epsf-form-sheet__audit-heading">Activity</h3>
                  <FormActivityLog rows={auditRows} loading={auditLoading} />
                </aside>
              ) : null}
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={busy}
            >
              {isReadOnly ? "Close" : "Cancel"}
            </button>
            {isReview ? (
              <>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={onReject}
                  disabled={busy}
                >
                  Reject
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onApprove}
                  disabled={busy}
                >
                  {busy ? "Approving…" : "Approve & lock"}
                </button>
              </>
            ) : null}
            {!isReadOnly ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={onClassicSubmit}
                disabled={busy || loading || !instance || !isEditable}
              >
                {busy ? "Submitting…" : "Submit form"}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
    <InfoAlert {...alertState} onClose={hideAlert} />
    </>,
    document.body,
  );
}
